import { useEffect, useMemo, useState } from "react";
import { api, formatCurrency } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ReferenceDot,
} from "recharts";
import { Loader2, TrendingDown, ChevronDown, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { colorOf } from "./contractColors";

const MONTH_LABELS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const formatLabel = (label, view) => {
  if (view === "yearly") return label;
  const [y, m] = label.split("-");
  return `${MONTH_LABELS[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

const HORIZON_PRESETS = [
  { value: 12, label: "12 mois" },
  { value: 24, label: "2 ans" },
  { value: 60, label: "5 ans" },
  { value: 120, label: "10 ans" },
];

const ContractMultiSelect = ({ contracts, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const toggle = (id) => {
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    onChange(next);
  };
  const label = selected.length === 0
    ? "Sélectionner des contrats à simuler"
    : `${selected.length} contrat${selected.length > 1 ? "s" : ""} sélectionné${selected.length > 1 ? "s" : ""}`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-between" data-testid="simulator-trigger">
          <span className="truncate text-sm">{label}</span>
          <ChevronDown className="h-4 w-4 ml-1 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <div className="p-2 border-b flex items-center justify-between">
          <span className="text-xs font-medium">Quels contrats arrêter ?</span>
          {selected.length > 0 && (
            <Button size="sm" variant="ghost" onClick={() => onChange([])} className="h-7 text-xs">
              Tout désélectionner
            </Button>
          )}
        </div>
        <div className="max-h-[300px] overflow-auto p-1">
          {contracts.length === 0 ? (
            <div className="text-center text-xs text-muted-foreground py-4">Aucun contrat</div>
          ) : contracts.map((c) => (
            <label
              key={c.id}
              className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
              data-testid={`sim-opt-${c.id}`}
            >
              <Checkbox checked={selected.includes(c.id)} onCheckedChange={() => toggle(c.id)} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{c.title}</div>
                <div className="text-[11px] text-muted-foreground">
                  {formatCurrency(c.monthly_amount || 0)}/mois · fin: {c.end_date || "—"}
                </div>
              </div>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const ContractsForecastChart = ({ contracts }) => {
  const [view, setView] = useState("monthly");
  const [horizon, setHorizon] = useState(24);
  const [excluded, setExcluded] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = { view, horizon_months: horizon };
        if (excluded.length > 0) params.contract_ids = excluded.join(",");
        const { data } = await api.getContractsForecast(params);
        if (!cancelled) setData(data);
      } catch (_e) {
        if (!cancelled) toast.error("Erreur chargement prévision");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [view, horizon, excluded]);

  const chartData = useMemo(() => {
    if (!data?.series) return [];
    return data.series.map((d) => ({
      label: formatLabel(d.label, view),
      raw: d.label,
      total: d.total,
      scenario: d.scenario_total,
    }));
  }, [data, view]);

  const dropMarkers = useMemo(() => {
    if (!data?.drop_events || view === "yearly") return [];
    return data.drop_events.map((e) => {
      const point = chartData.find((p) => p.raw === e.label);
      if (!point) return null;
      return { x: point.label, y: point.total, drop: e.amount_drop, contracts: e.contracts };
    }).filter(Boolean);
  }, [data, chartData, view]);

  const savings = data?.scenario || { savings_monthly: 0, savings_annual: 0, excluded_count: 0 };
  const hasScenario = excluded.length > 0;

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingDown className="h-5 w-5 text-indigo-600" />
            Prévision des charges
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-md border bg-white p-0.5">
              <Button
                size="sm"
                variant={view === "monthly" ? "default" : "ghost"}
                onClick={() => setView("monthly")}
                className="h-7 px-3 text-xs"
                data-testid="forecast-view-monthly"
              >
                Mensuel
              </Button>
              <Button
                size="sm"
                variant={view === "yearly" ? "default" : "ghost"}
                onClick={() => setView("yearly")}
                className="h-7 px-3 text-xs"
                data-testid="forecast-view-yearly"
              >
                Annuel
              </Button>
            </div>
            <Select value={String(horizon)} onValueChange={(v) => setHorizon(parseInt(v, 10))}>
              <SelectTrigger className="w-[110px] h-8" data-testid="forecast-horizon">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HORIZON_PRESETS.map((h) => (
                  <SelectItem key={h.value} value={String(h.value)}>{h.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Simulation panel */}
        <div className="rounded-lg border border-dashed border-indigo-200 bg-indigo-50/50 p-3 space-y-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-600" />
            <Label className="text-sm font-medium">Simulateur "Et si j'arrête ?"</Label>
          </div>
          <ContractMultiSelect contracts={contracts} selected={excluded} onChange={setExcluded} />
          {hasScenario && (
            <div className="flex flex-wrap gap-2 pt-1" data-testid="savings-summary">
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 border font-mono">
                Économie: {formatCurrency(savings.savings_monthly)}/mois
              </Badge>
              <Badge className="bg-emerald-600 text-white font-mono">
                {formatCurrency(savings.savings_annual)}/an
              </Badge>
            </div>
          )}
        </div>

        {/* Chart */}
        <div className="h-[340px] w-full">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
            </div>
          ) : chartData.length === 0 ? (
            <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
              Aucune donnée à afficher
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 16, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="totalGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#6366f1" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="scenGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v).toLocaleString("fr-FR")} €`} width={80} />
                <Tooltip
                  formatter={(value, name) => [formatCurrency(value), name]}
                  labelClassName="font-medium"
                  contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area
                  type="monotone"
                  dataKey="total"
                  name="Charges actuelles"
                  stroke="#6366f1"
                  strokeWidth={2}
                  fill="url(#totalGrad)"
                />
                {hasScenario && (
                  <Area
                    type="monotone"
                    dataKey="scenario"
                    name="Charges après arrêt"
                    stroke="#10b981"
                    strokeWidth={2}
                    strokeDasharray="6 3"
                    fill="url(#scenGrad)"
                  />
                )}
                {dropMarkers.map((m, i) => (
                  <ReferenceDot
                    key={i}
                    x={m.x}
                    y={m.y}
                    r={5}
                    fill="#ef4444"
                    stroke="#fff"
                    strokeWidth={2}
                    ifOverflow="extendDomain"
                  >
                    <title>{`-${formatCurrency(m.drop)} (${m.contracts.map(c => c.title).join(", ")})`}</title>
                  </ReferenceDot>
                ))}
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Drop events legend */}
        {data?.drop_events?.length > 0 && (
          <div className="rounded-lg bg-slate-50 p-3 space-y-1.5" data-testid="drop-events">
            <div className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-red-500" /> Baisses prévues
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
              {data.drop_events.slice(0, 8).map((e, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-slate-600 truncate">
                    {formatLabel(e.label, "monthly")} — {e.contracts.map(c => c.title).join(", ")}
                  </span>
                  <span className="font-mono text-red-600 shrink-0 ml-2">−{formatCurrency(e.amount_drop)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ContractsForecastChart;
